import { Link } from "react-router-dom";
import { ArrowRight, Facebook } from "lucide-react";
import SectionLabel from "@/components/SectionLabel";
import { FIESTA_POSTS, FIESTA_ALBUM_URL, PARISH_FB_PAGE } from "@/data/fiestaPosts";

const embedSrc = (url) =>
  `https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(url)}&show_text=true&width=500`;

const FiestaEmbed = ({ url, index }) => (
  <figure
    data-testid={`fiesta-post-${index}`}
    className="break-inside-avoid mb-6 bg-parchment-50 border border-parchment-400 p-3 shadow-[0_10px_30px_-18px_rgba(60,40,20,0.45)] hover:-translate-y-1 transition-transform duration-500"
  >
    <iframe
      title={`8th Patronal Fiesta photo post ${index + 1}`}
      src={embedSrc(url)}
      className="w-full border-0 bg-white"
      style={{ height: 640 }}
      scrolling="no"
      loading="lazy"
      allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
    />
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`fiesta-post-link-${index}`}
      className="mt-3 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-cocoa-700 hover:text-terracotta transition-colors font-sans"
    >
      <Facebook size={13} /> View on Facebook
    </a>
  </figure>
);

const pageFeedSrc = `https://www.facebook.com/plugins/page.php?href=${encodeURIComponent(
  PARISH_FB_PAGE
)}&tabs=timeline&width=500&height=720&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=false`;

const AlbumFeed = () => (
  <div data-testid="fiesta-album-feed" className="grid lg:grid-cols-12 gap-10 items-start">
    <div className="lg:col-span-5 bg-cocoa-900 text-parchment-100 p-10 md:p-12 relative overflow-hidden">
      <div className="absolute -top-8 -right-4 font-serif text-[12rem] text-gold/10 leading-none select-none">✿</div>
      <div className="relative text-[11px] uppercase tracking-[0.28em] text-gold font-sans mb-4">Official Photo Album</div>
      <h3 className="relative font-serif text-3xl md:text-4xl leading-tight mb-5">
        Relive the 8th Patronal Fiesta
      </h3>
      <p className="relative font-sans font-light text-parchment-200/85 leading-relaxed mb-8">
        Browse every photograph from the Holy Mass, procession, and parish
        celebration in our official Facebook album.
      </p>
      <a
        href={FIESTA_ALBUM_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="fiesta-open-album-btn"
        className="relative inline-flex items-center gap-3 px-8 py-3.5 rounded-full bg-gold text-cocoa-900 text-[12px] uppercase tracking-[0.22em] font-sans hover:bg-parchment-100 transition-colors duration-300"
      >
        <Facebook size={15} /> Open the Fiesta Album
      </a>
    </div>
    <div className="lg:col-span-7 flex justify-center">
      <div className="w-full max-w-[500px] bg-parchment-50 border border-parchment-400 p-3 shadow-[0_10px_30px_-18px_rgba(60,40,20,0.45)]">
        <iframe
          title="Saint Francis of Assisi Parish Facebook feed"
          data-testid="fiesta-page-feed"
          src={pageFeedSrc}
          className="w-full border-0 bg-white"
          style={{ height: 720 }}
          scrolling="no"
          loading="lazy"
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
        />
        <a
          href={PARISH_FB_PAGE}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="fiesta-page-feed-fallback"
          className="mt-3 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-cocoa-700 hover:text-terracotta transition-colors font-sans"
        >
          <Facebook size={13} /> Feed not showing? Visit our Facebook page
        </a>
      </div>
    </div>
  </div>
);

export default function FiestaGallery({ preview = false }) {
  const posts = preview ? FIESTA_POSTS.slice(0, 3) : FIESTA_POSTS;
  return (
    <section
      id="fiesta"
      data-testid="fiesta-gallery-section"
      className="py-24 md:py-32 bg-parchment-100 relative grain overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(176,90,60,0.07),transparent_55%)]" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <SectionLabel
          kicker="October 4 · Feast of Saint Francis"
          title="8th Patronal Fiesta"
          intro="Moments of prayer, procession, and celebration from our parish's eighth patronal fiesta — shared straight from our Facebook page."
        />
        {posts.length === 0 ? (
          <div className="mt-16"><AlbumFeed /></div>
        ) : (
          <div
            data-testid="fiesta-gallery-grid"
            className={`mt-16 mx-auto ${posts.length > 1 ? "columns-1 md:columns-2 lg:columns-3 gap-6" : "max-w-xl"}`}
          >
            {posts.map((url, i) => (
              <FiestaEmbed key={`${url}-${i}`} url={url} index={i} />
            ))}
          </div>
        )}
        {preview && FIESTA_POSTS.length > 0 && (
          <div className="mt-10 text-center">
            <Link
              to="/fiesta"
              data-testid="fiesta-view-all-btn"
              className="inline-flex items-center gap-3 px-8 py-3.5 rounded-full bg-cocoa-900 text-parchment-100 text-[12px] uppercase tracking-[0.22em] font-sans hover:bg-terracotta transition-colors duration-300"
            >
              View the full gallery <ArrowRight size={15} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
